import {makeAutoObservable, observable} from 'mobx';
import type {LibraryVoice} from '../../../types';
import {
  defaultDialect,
  defaultVoice,
  dialectLabel,
  libraryOnly,
} from '../../../studioLanguages';
import {VOICES, isLibraryVoice} from '../createOptions';
import LibraryVoicesStore from './LibraryVoicesStore';
import type CreateStore from './CreateStore';

/**
 * Who narrates the microreels: the language and dialect, Emma, James or a voice from the
 * voice library, and a library voice's delivery. The choices themselves are part of the page's
 * form (they go in the brief as they are); this store keeps what goes with them.
 */
class NarrationStore {
  public readonly libraryVoices = new LibraryVoicesStore();
  // The library voice chosen; choosing another voice or language clears it.
  public libraryVoice: LibraryVoice | null = null;
  // A library voice's delivery; Natural ('') lets the voice follow the script.
  public voiceEmotion = '';

  constructor(private readonly page: CreateStore) {
    makeAutoObservable<NarrationStore, 'page'>(
      this,
      {page: false, libraryVoices: false, libraryVoice: observable.ref},
      {autoBind: true},
    );
  }

  /** Emma and James don't speak every language; a library voice narrates the rest. */
  public get needsLibraryVoice() {
    return libraryOnly(this.page.form.language);
  }

  /**
   * The voices to choose from: Emma and James first where they speak the narration language
   * and dialect, then the library's in the server's order. A server that doesn't list Emma and
   * James yet, or a list that can't be read, still offers them, without a sample.
   */
  public get voices(): LibraryVoice[] {
    const {form, options} = this.page;
    const listed = this.libraryVoices.voices ?? [];
    // Emma or James where the server's list lacks them, described as in /options.
    const ownVoice = (id: string, gender: 'female' | 'male'): LibraryVoice => ({
      id,
      name: id,
      description:
        options.voices?.find(({name}) => name === id)?.description ?? '',
      gender,
      accent: dialectLabel(form.language, form.region),
    });
    const own = defaultVoice(form.language, form.region)
      ? VOICES.map(
          ({id, gender}) =>
            listed.find(voice => voice.id === id) ?? ownVoice(id, gender),
        )
      : [];
    return [...own, ...listed.filter(voice => !own.includes(voice))];
  }

  /** A library voice takes a delivery in English only. */
  public get deliveries() {
    return this.page.options.voiceEmotions ?? [];
  }

  public get showDelivery() {
    const {form, clipping} = this.page;
    return (
      !clipping &&
      isLibraryVoice(form.voiceName) &&
      form.language === 'en' &&
      this.deliveries.length > 0
    );
  }

  /** The voices show, in the open customize section of a reel that is narrated. */
  public get showVoices() {
    const {customize, clipping} = this.page;
    return customize && !clipping;
  }

  public selectLanguage(language: string) {
    // Picking the current language again keeps its dialect and voice.
    if (language === this.page.form.language) return;
    const region = defaultDialect(language);
    const voice = defaultVoice(language, region);
    // A library voice speaks its own language, so another language starts on its default voice,
    // or on none where Emma and James don't speak it.
    this.libraryVoice = null;
    this.page.patch({language, region, voiceName: voice ?? ''});
  }

  public selectDialect(region: string) {
    const {form} = this.page;
    // A dialect of the same language keeps a voice chosen from the library.
    if (isLibraryVoice(form.voiceName)) return this.page.patch({region});
    const voice = defaultVoice(form.language, region);
    this.page.patch(voice ? {region, voiceName: voice} : {region});
  }

  public setVoiceEmotion(voiceEmotion: string) {
    this.voiceEmotion = voiceEmotion;
  }

  /** Emma, James or a library voice, sent as the brief's voiceName. */
  public chooseVoice(voice: LibraryVoice) {
    this.libraryVoice = isLibraryVoice(voice.id) ? voice : null;
    this.page.patch({voiceName: voice.id});
  }

  /** Lists the voices of the narration language and dialect. */
  public loadVoices() {
    const {language, region} = this.page.form;
    this.libraryVoices.load(this.page.listVoices, {language, region});
  }

  public reset() {
    this.libraryVoices.reset();
    this.libraryVoice = null;
    this.voiceEmotion = '';
  }
}

export default NarrationStore;
